import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useHistory } from 'react-router-dom';
import UnauthenticatedRouteLanguageContext from '../services/UnauthenticatedRouteLanguageContext';
import useRecaptchaSiteKey from '../services/useRecaptchaSiteKey';
import LoadingScreenContext from '../services/LoadingScreenContext';
import { navigateToErrorPage } from '../services/errorUtils';

// Maps VeevaID languages to Google Recaptcha's language values: https://developers.google.com/recaptcha/docs/language
const GOOGLE_LANGUAGE_MAP = {
    kr: 'ko',
};

const recaptchaScriptLoaded = () => !!window?.grecaptcha?.render;

const ELEMENT_ID = 'veeva-id-g-recaptcha';

const propTypes = {
    /**
     * Ref callback for setting the execute function to a variable
     */
    execute: PropTypes.func,
    /**
     * Ref callback for setting the reset function to a variable
     */
    reset: PropTypes.func,
};
const RecaptchaV2Invisible = ({ execute, reset }) => {
    const { setLoading: setLoadingScreen } = useContext(LoadingScreenContext);
    const { messageLanguage } = useContext(UnauthenticatedRouteLanguageContext);
    const [recaptchaLoaded, setRecaptchaLoaded] = useState(recaptchaScriptLoaded());
    const challengeCallbacks = useRef({});
    const [siteKey, siteKeyError] = useRecaptchaSiteKey();
    const history = useHistory();
    const widgetIdRef = useRef();

    const googleRecaptchaLanguage = useMemo(() => {
        return GOOGLE_LANGUAGE_MAP[messageLanguage] ?? messageLanguage;
    }, [messageLanguage]);

    useEffect(() => {
        let checkLoadingInterval = null;
        if (!recaptchaScriptLoaded()) {
            checkLoadingInterval = setInterval(() => {
                if (recaptchaScriptLoaded()) {
                    setRecaptchaLoaded(true);
                    clearInterval(checkLoadingInterval);
                }
            }, 1000);
        }
        return () => {
            clearInterval(checkLoadingInterval);
        };
    }, []);

    const handleExecute = (widgetId) => {
        return new Promise((resolve, reject) => {
            challengeCallbacks.current = { resolve, reject };
            window.grecaptcha.execute(widgetId);
        });
    };

    const handleChange = (token) => {
        challengeCallbacks.current.resolve(token);
    };

    const handleExpire = () => {
        handleChange(null);
    };

    const handleError = () => {
        challengeCallbacks.current.reject();
    };

    useEffect(() => {
        setLoadingScreen(true);
        if (siteKeyError) {
            setLoadingScreen(false);
            navigateToErrorPage(history, siteKeyError);
        }
        if (recaptchaLoaded && siteKey) {
            setLoadingScreen(false);
            // Google API mutates the DOM using this function
            widgetIdRef.current = window.grecaptcha.render(ELEMENT_ID, {
                sitekey: siteKey,
                size: 'invisible',
                hl: googleRecaptchaLanguage,
                callback: handleChange,
                'expired-callback': handleExpire,
                'error-callback': handleError,
            });
            reset(() => window.grecaptcha.reset(widgetIdRef.current));
            execute(() => handleExecute(widgetIdRef.current));
        }
        // Ignoring deps on setLoadingScreen/history because they shouldn't
        // affect when this component starts/stops loading
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [recaptchaLoaded, siteKey, siteKeyError]);

    useEffect(() => {
        if (recaptchaScriptLoaded && widgetIdRef.current !== undefined) {
            window.grecaptcha.reset(widgetIdRef.current, { hl: googleRecaptchaLanguage });
        }
    }, [googleRecaptchaLanguage]);

    // Only used as an insertion point for the Google API
    return <div id={ELEMENT_ID} />;
};

RecaptchaV2Invisible.propTypes = propTypes;
export default RecaptchaV2Invisible;
